import { useQuery } from "react-query";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { MediaAnalyticsApiParams } from "@circle-react/helpers/urlHelpers/internalApi/analytics";

interface AnalyticsParams {
  dateGrouping?: string;
  endDate?: string;
  signedId: string;
  startDate?: string;
}

type MetricType =
  | "views"
  | "unique_viewers"
  | "average_progress"
  | "completion_rate";

/**
 * Generic hook for fetching video analytics metrics
 */
export const useVideoAnalyticsMetric = (
  metricType: MetricType,
  { signedId, startDate, endDate, dateGrouping }: AnalyticsParams,
) => {
  const params: MediaAnalyticsApiParams = {
    signed_id: signedId,
  };

  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (dateGrouping) params.date_grouping = dateGrouping;

  const fetchMetric = (): Promise<number> =>
    reactQueryGet(internalApi.analytics.media.overview[metricType](params));

  return useQuery(
    ["video-analytics", metricType, signedId, startDate, endDate, dateGrouping],
    fetchMetric,
    {
      enabled: Boolean(signedId),
    },
  );
};

/**
 * Specific metric hooks - convenient wrappers around the generic hook
 */
export const useVideoAnalyticsViews = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("views", params);

export const useVideoAnalyticsUniqueViewers = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("unique_viewers", params);

export const useVideoAnalyticsAverageProgress = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("average_progress", params);

export const useVideoAnalyticsCompletionRate = (params: AnalyticsParams) =>
  useVideoAnalyticsMetric("completion_rate", params);
